import { Alert, Button, Card, Drawer, Form, Input, InputNumber, Select, Space, Switch, Table, Tag, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  createAdminFactory,
  createAdminServiceLocation,
  listFactories,
  listOrganizations,
  listServiceLocations,
  updateAdminFactory,
  updateAdminServiceLocation,
} from '../services/api';
import type {
  CurrentUser,
  Factory,
  FactoryUpsert,
  Organization,
  ServiceLocation,
  ServiceLocationUpsert,
} from '../types/incident';

const { Text } = Typography;

type AdminMasterPageProps = {
  currentUser: CurrentUser | null;
  kind: 'factories' | 'serviceLocations';
};

type MasterRow = Factory | ServiceLocation;
type MasterFormValues = FactoryUpsert & ServiceLocationUpsert;

const cleanString = (value?: string | null): string | null => {
  if (value === undefined || value === null) return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
};

const toFactoryPayload = (values: MasterFormValues): FactoryUpsert => ({
  organization_id: values.organization_id,
  name: values.name.trim(),
  code: values.code.trim(),
  country_code: cleanString(values.country_code),
  region_code: cleanString(values.region_code),
  address: cleanString(values.address),
  is_hvac: values.is_hvac ?? null,
  production_products: cleanString(values.production_products),
  notes: cleanString(values.notes),
  official_url: cleanString(values.official_url),
  latitude: values.latitude ?? null,
  longitude: values.longitude ?? null,
});

const toServiceLocationPayload = (values: MasterFormValues): ServiceLocationUpsert => ({
  organization_id: values.organization_id,
  region_code: values.region_code.trim(),
  name: values.name.trim(),
  code: values.code.trim(),
  address: cleanString(values.address),
  service_offerings: cleanString(values.service_offerings),
  contact_info: cleanString(values.contact_info),
  official_url: cleanString(values.official_url),
  latitude: values.latitude ?? null,
  longitude: values.longitude ?? null,
});

const AdminMasterPage = ({ currentUser, kind }: AdminMasterPageProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<MasterFormValues>();
  const [messageApi, contextHolder] = message.useMessage();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [factories, setFactories] = useState<Factory[]>([]);
  const [serviceLocations, setServiceLocations] = useState<ServiceLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingRow, setEditingRow] = useState<MasterRow | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [keyword, setKeyword] = useState('');

  const isSystemAdmin = useMemo(
    () => currentUser?.assignments.some((assignment) => assignment.role === 'SYSTEM_ADMIN' && assignment.is_active) || false,
    [currentUser?.assignments]
  );
  const isFactories = kind === 'factories';
  const rows: MasterRow[] = isFactories ? factories : serviceLocations;

  const loadMasters = useCallback(async () => {
    setLoading(true);
    try {
      const [nextOrganizations, nextFactories, nextServiceLocations] = await Promise.all([
        listOrganizations(),
        listFactories(),
        listServiceLocations(),
      ]);
      setOrganizations(nextOrganizations);
      setFactories(nextFactories);
      setServiceLocations(nextServiceLocations);
    } catch {
      messageApi.error(t('adminMaster.messages.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [messageApi, t]);

  useEffect(() => {
    if (isSystemAdmin) {
      loadMasters();
    } else {
      setLoading(false);
    }
  }, [isSystemAdmin, loadMasters]);

  const filteredRows = useMemo(() => {
    const normalized = keyword.trim().toLowerCase();
    if (!normalized) return rows;
    return rows.filter((row) =>
      [row.name, row.code, row.organization_name, row.region_code, row.address]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(normalized))
    );
  }, [keyword, rows]);

  const openCreateDrawer = () => {
    setEditingRow(null);
    form.resetFields();
    form.setFieldsValue({ is_hvac: false } as Partial<MasterFormValues>);
    setDrawerOpen(true);
  };

  const openEditDrawer = (row: MasterRow) => {
    setEditingRow(row);
    form.setFieldsValue({
      ...row,
      is_hvac: 'is_hvac' in row ? row.is_hvac ?? false : false,
    } as Partial<MasterFormValues>);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingRow(null);
    form.resetFields();
  };

  const handleSubmit = async (values: MasterFormValues) => {
    setSaving(true);
    try {
      if (isFactories) {
        const saved = editingRow
          ? await updateAdminFactory(editingRow.id, toFactoryPayload(values))
          : await createAdminFactory(toFactoryPayload(values));
        setFactories((current) => [saved, ...current.filter((row) => row.id !== saved.id)].sort((a, b) => a.name.localeCompare(b.name)));
      } else {
        const saved = editingRow
          ? await updateAdminServiceLocation(editingRow.id, toServiceLocationPayload(values))
          : await createAdminServiceLocation(toServiceLocationPayload(values));
        setServiceLocations((current) =>
          [saved, ...current.filter((row) => row.id !== saved.id)].sort((a, b) => a.name.localeCompare(b.name))
        );
      }
      messageApi.success(editingRow ? t('adminMaster.messages.updated') : t('adminMaster.messages.created'));
      closeDrawer();
      await loadMasters();
    } catch {
      messageApi.error(t('adminMaster.messages.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const columns: ColumnsType<MasterRow> = [
    {
      title: isFactories ? t('factory.columns.name') : t('serviceLocation.columns.name'),
      dataIndex: 'name',
      key: 'name',
      width: 240,
      render: (_value, row) => (
        <Space direction="vertical" size={2}>
          <Text strong>{row.name}</Text>
          <Text type="secondary">{row.code}</Text>
        </Space>
      ),
    },
    {
      title: t('master.filters.organization'),
      dataIndex: 'organization_name',
      key: 'organization_name',
      width: 180,
    },
    {
      title: t('master.filters.region'),
      dataIndex: 'region_code',
      key: 'region_code',
      width: 120,
      render: (value?: string | null) => (value ? <Tag>{value}</Tag> : '-'),
    },
    {
      title: t('factory.columns.address'),
      dataIndex: 'address',
      key: 'address',
      ellipsis: true,
    },
    {
      title: t('adminMaster.table.location'),
      key: 'location',
      width: 150,
      render: (_value, row) => (
        <Text type="secondary">
          {row.latitude != null && row.longitude != null ? `${row.latitude}, ${row.longitude}` : '-'}
        </Text>
      ),
    },
    {
      title: t('adminMaster.table.actions'),
      key: 'actions',
      width: 100,
      render: (_value, row) => (
        <Button size="small" onClick={() => openEditDrawer(row)}>
          {t('adminMaster.actions.edit')}
        </Button>
      ),
    },
  ];

  if (!isSystemAdmin) {
    return (
      <div className="page">
        {contextHolder}
        <Alert type="warning" showIcon message={t('adminMaster.forbidden')} />
      </div>
    );
  }

  return (
    <div className="page adminMasterPage">
      {contextHolder}
      <div className="pageHeader">
        <p>{isFactories ? t('adminMaster.factories.description') : t('adminMaster.serviceLocations.description')}</p>
      </div>

      <Card
        className="adminMasterCard"
        title={isFactories ? t('adminMaster.factories.listTitle') : t('adminMaster.serviceLocations.listTitle')}
        extra={
          <Button type="primary" onClick={openCreateDrawer}>
            {isFactories ? t('adminMaster.factories.create') : t('adminMaster.serviceLocations.create')}
          </Button>
        }
      >
        <Input.Search
          className="adminMasterSearch"
          allowClear
          placeholder={t('master.filters.keyword')}
          value={keyword}
          onChange={(event) => setKeyword(event.target.value)}
        />
        <Table
          rowKey="id"
          columns={columns}
          dataSource={filteredRows}
          loading={loading}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 980 }}
        />
      </Card>

      <Drawer
        title={editingRow ? t('adminMaster.form.editTitle') : t('adminMaster.form.createTitle')}
        open={drawerOpen}
        onClose={closeDrawer}
        width={560}
        destroyOnClose
        extra={
          <Space>
            <Button onClick={closeDrawer}>{t('common.cancel')}</Button>
            <Button type="primary" loading={saving} onClick={() => form.submit()}>
              {editingRow ? t('adminMaster.actions.update') : t('adminMaster.actions.create')}
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="organization_id" label={t('master.filters.organization')} rules={[{ required: true }]}>
            <Select
              showSearch
              optionFilterProp="label"
              options={organizations.map((organization) => ({
                value: organization.id,
                label: `${organization.name} (${organization.code})`,
              }))}
            />
          </Form.Item>
          <Form.Item name="name" label={isFactories ? t('factory.columns.name') : t('serviceLocation.columns.name')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="code" label={t('adminMaster.form.code')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          {isFactories && (
            <Form.Item name="country_code" label={t('factory.columns.country')}>
              <Input maxLength={2} />
            </Form.Item>
          )}
          <Form.Item name="region_code" label={t('master.filters.region')} rules={isFactories ? undefined : [{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="address" label={t('factory.columns.address')}>
            <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} />
          </Form.Item>
          {isFactories ? (
            <>
              <Form.Item name="is_hvac" label={t('factory.columns.hvac')} valuePropName="checked">
                <Switch />
              </Form.Item>
              <Form.Item name="production_products" label={t('factory.columns.productionProducts')}>
                <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} />
              </Form.Item>
              <Form.Item name="notes" label={t('factory.columns.notes')}>
                <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} />
              </Form.Item>
            </>
          ) : (
            <>
              <Form.Item name="service_offerings" label={t('serviceLocation.columns.serviceOfferings')}>
                <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} />
              </Form.Item>
              <Form.Item name="contact_info" label={t('serviceLocation.columns.contactInfo')}>
                <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} />
              </Form.Item>
            </>
          )}
          <Form.Item name="official_url" label={t('master.actions.officialUrl')}>
            <Input />
          </Form.Item>
          <Space className="adminMasterCoordinateFields" size={12}>
            <Form.Item name="latitude" label={t('factory.columns.latitude')}>
              <InputNumber controls={false} />
            </Form.Item>
            <Form.Item name="longitude" label={t('factory.columns.longitude')}>
              <InputNumber controls={false} />
            </Form.Item>
          </Space>
        </Form>
      </Drawer>
    </div>
  );
};

export default AdminMasterPage;
